import React, { useState, useEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { ProgramsService } from '../../services/programsService';
import { ProgramModel } from '../../types/program';
import AddProgramDialog from './AddProgramDialog';
import EditProgramDialog from './EditProgramDialog';
import DeleteProgramDialog from './DeleteProgramDialog';

import { Plus, ExternalLink, Edit, Trash2 } from 'lucide-react';

import { toast } from 'react-toastify';

const Programs = () => {
  const [programs, setPrograms] = useState<ProgramModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedProgram, setSelectedProgram] = useState<ProgramModel | null>(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setIsLoading(true);
      const programsData = await ProgramsService.loadPrograms();
      setPrograms(programsData);
    } catch (error) {
      console.error('Error loading programs:', error);
      toast.error('Failed to load programs. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProgram = () => {
    setShowAddDialog(true);
  };

  const handleEditProgram = (program: ProgramModel) => {
    setSelectedProgram(program);
    setShowEditDialog(true);
  };

  const handleDeleteProgram = (program: ProgramModel) => {
    setSelectedProgram(program);
    setShowDeleteDialog(true);
  };

  const handleSaveProgram = async (program: ProgramModel) => {
    try {
      await ProgramsService.createProgram(program);
      await loadPrograms();
      toast.success('Program added successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error('Failed to add program. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdateProgram = async (updatedProgram: ProgramModel) => {
    try {
      await ProgramsService.updateProgram(updatedProgram);
      await loadPrograms();
      toast.success('Program updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error('Failed to update program. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDeleteProgramConfirm = async () => {
    if (selectedProgram) {
      try {
        await ProgramsService.deleteProgram(selectedProgram.id);
        await loadPrograms();
        toast.success('Program deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('Error deleting program:', error);
        toast.error('Failed to delete program. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        throw error;
      }
    }
  };

  const handleCloseAddDialog = () => setShowAddDialog(false);
  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setSelectedProgram(null);
  };
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedProgram(null);
  };

  const openProgramLink = (url: string) => {
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = 'https://' + url;
    }
    window.open(fullUrl, '_blank');
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Programs' pageTitle='Dashboard' />
      <div className="col-span-12 card">
        <div className="card-body">
          <div className="flex flex-row items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
              <h6 className="text-15 font-semibold">All Programs</h6>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleAddProgram}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 flex items-center"
                title="Add new program"
              >
                <Plus className="size-4 md:mr-1" />
                <span className="hidden md:inline">Add New Program</span>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : programs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-zink-200">No programs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                  <tr>
                    <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left w-80">
                      Programs
                    </th>
                    <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left">
                      Description
                    </th>
                    <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-center w-32">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((program, index) => (
                    <tr key={program.id} className="border-t border-slate-200 dark:border-zink-500 hover:bg-slate-50 dark:hover:bg-zink-700/50">
                      <td className="px-4 py-3 first:pl-6 last:pr-6 w-80">
                        <div className="flex items-center gap-3">
                          <div className="size-10 bg-slate-100 rounded-full dark:bg-zink-600 shrink-0">
                            <img
                              src={program.photoUrl}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                if (nextElement) nextElement.style.display = 'flex';
                              }}
                            />
                            <div className="size-10 bg-gray-100 rounded-full dark:bg-gray-500/20 shrink-0 items-center justify-center hidden">
                              <span className="text-gray-500 text-lg">📚</span>
                            </div>
                          </div>
                          <div className="break-words">
                            {program.title}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 first:pl-6 last:pr-6">
                        <div className="break-words">
                          {program.description}
                        </div>
                      </td>
                      <td className="px-4 py-3 first:pl-6 last:pr-6">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openProgramLink(program.url)}
                            className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/20"
                            title="Open link"
                          >
                            <ExternalLink className="size-4" />
                          </button>
                          <button
                            onClick={() => handleEditProgram(program)}
                            className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                            title="Edit program"
                          >
                            <Edit className="size-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProgram(program)}
                            className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                            title="Delete program"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AddProgramDialog
        onClose={handleCloseAddDialog}
        onSave={handleSaveProgram}
        show={showAddDialog}
      />

      {selectedProgram && (
        <EditProgramDialog
          program={selectedProgram}
          onClose={handleCloseEditDialog}
          onSave={handleUpdateProgram}
          show={showEditDialog}
        />
      )}

      {selectedProgram && (
        <DeleteProgramDialog
          program={selectedProgram}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDeleteProgramConfirm}
          show={showDeleteDialog}
        />
      )}
    </React.Fragment>
  );
};

export default Programs;